import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Save, Loader2, Upload, X, Image as ImageIcon, Video, FileVideo, Link as LinkIcon, AlertCircle, Newspaper, CheckCircle } from 'lucide-react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import api from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../components/ui/toast';

// Sub-component for Drag & Drop Upload
const MediaUploader = ({ label, type, currentUrl, onFileSelect, onLinkSet, onClear, progress, error }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [tempLink, setTempLink] = useState('');

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setIsDragging(true);
        } else if (e.type === 'dragleave') {
            setIsDragging(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            onFileSelect(e.dataTransfer.files[0]);
        }
    }, [onFileSelect]);

    const handleChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            onFileSelect(e.target.files[0]);
        }
    };

    const isVideo = type === 'video';
    const accept = isVideo ? "video/*" : "image/*";
    const Icon = isVideo ? FileVideo : ImageIcon;

    return (
        <div className="space-y-2">
            <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                {!currentUrl && (
                    <button
                        type="button"
                        onClick={() => setShowLinkInput(!showLinkInput)}
                        className="text-xs text-[#2A9D8F] hover:underline flex items-center gap-1"
                    >
                        <LinkIcon className="w-3 h-3" />
                        {showLinkInput ? 'Switch to Upload' : 'Use External Link'}
                    </button>
                )}
            </div>

            {currentUrl ? (
                <div className="relative group border border-gray-200 rounded-xl overflow-hidden bg-gray-50 p-1">
                    {isVideo ? (
                        (currentUrl.startsWith('data:video/') || currentUrl.includes('firebasestorage') || currentUrl.match(/\.(mp4|webm)$/) || currentUrl.includes('youtube') || currentUrl.includes('youtu.be')) ? (
                            <div className="aspect-video bg-black rounded-lg overflow-hidden">
                                {currentUrl.includes('youtube') || currentUrl.includes('youtu.be') ? (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white flex-col gap-2">
                                        <Video className="w-8 h-8 opacity-50" />
                                        <span className="text-xs">YouTube Video Linked</span>
                                    </div>
                                ) : (
                                    <video src={currentUrl} controls className="w-full h-full object-contain" />
                                )}
                            </div>
                        ) : (
                            <div className="w-full h-48 flex items-center justify-center text-gray-400 bg-gray-100 italic text-xs p-4 text-center">
                                Link: {currentUrl.length > 50 ? currentUrl.substring(0, 50) + '...' : currentUrl}
                            </div>
                        )
                    ) : (
                        <img src={currentUrl} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                    )}

                    <button
                        type="button"
                        onClick={onClear}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition-colors z-10"
                        title="Remove"
                    >
                        <X className="w-4 h-4" />
                    </button>
                    <div className="mt-2 text-[10px] text-gray-400 truncate px-1">
                        {currentUrl.startsWith('data:') ? '📁 File uploaded (embedded)' : currentUrl}
                    </div>
                </div>
            ) : showLinkInput ? (
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col gap-3">
                    <input
                        type="text"
                        placeholder={isVideo ? "Paste YouTube or Video URL here" : "Paste Image URL here"}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#2A9D8F] outline-none"
                        value={tempLink}
                        onChange={(e) => setTempLink(e.target.value)}
                    />
                    <button
                        type="button"
                        onClick={() => { if (tempLink) onLinkSet(tempLink); }}
                        className="bg-[#2A9D8F] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#238b7f]"
                    >
                        Apply Link
                    </button>
                </div>
            ) : (
                <div
                    className={`relative border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-colors min-h-[160px] ${isDragging ? 'border-[#2A9D8F] bg-[#2A9D8F]/5' : 'border-gray-300 hover:border-[#2A9D8F] hover:bg-gray-50'
                        }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <input
                        type="file"
                        accept={accept}
                        onChange={handleChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={progress > 0}
                    />

                    <div className={`p-4 rounded-full bg-gray-100 mb-3 ${isDragging ? 'text-[#2A9D8F]' : 'text-gray-400'}`}>
                        <Icon className="w-8 h-8" />
                    </div>

                    {progress > 0 ? (
                        <div className="w-full max-w-xs px-4">
                            <div className="flex justify-between text-xs mb-2">
                                <span className="text-[#2A9D8F] font-bold">Uploading...</span>
                                <span className="text-gray-600 font-bold">{Math.round(progress)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                                <div className="bg-[#2A9D8F] h-3 rounded-full transition-all duration-300 shadow-sm" style={{ width: `${progress}%` }}></div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <p className="text-sm font-bold text-gray-700">Drag & drop or Click to upload</p>
                            <p className="text-xs text-gray-500 mt-1">Supports {isVideo ? 'MP4, WebM' : 'JPG, PNG, WebP'}</p>
                        </>
                    )}

                    {error && (
                        <div className="mt-4 flex items-start gap-2 text-left bg-red-50 p-2 rounded border border-red-100 max-w-full overflow-hidden">
                            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-[10px] font-bold text-red-700 uppercase">Upload Error</p>
                                <p className="text-[10px] text-red-600 break-words line-clamp-2">{error}</p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const NewsEditorPage = () => {
    const { slug } = useParams();
    const isEditMode = !!slug;
    const navigate = useNavigate();
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);

    // Upload states
    const [imgProgress, setImgProgress] = useState(0);
    const [vidProgress, setVidProgress] = useState(0);
    const [uploadErrors, setUploadErrors] = useState({ image: '', video: '' });

    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
        defaultValues: {
            title: '',
            slug: '',
            category: 'News',
            date: new Date().toISOString().split('T')[0],
            summary: '',
            content: '',
            image_url: '',
            video_url: '',
            author: '',
            is_published: true
        }
    });

    const titleVal = watch('title');
    const imageUrl = watch('image_url');
    const videoUrl = watch('video_url');

    useEffect(() => {
        if (!isEditMode && titleVal) {
            const generatedSlug = titleVal.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
            setValue('slug', generatedSlug);
        }
    }, [titleVal, isEditMode, setValue]);

    useEffect(() => {
        if (isEditMode) fetchArticle();
    }, [slug]);

    const fetchArticle = async () => {
        try {
            const response = await api.news.getBySlug(slug);
            if (response.ok) {
                const data = await response.json();
                Object.keys(data).forEach(key => setValue(key, data[key]));
                if (data.date) setValue('date', data.date.split('T')[0]);
            } else {
                alert('Article not found');
                navigate('/admin/news');
            }
        } catch (error) {
            console.error(error);
        } finally {
            setFetching(false);
        }
    };

    const handleUpload = async (file, type) => {
        if (!file) return;

        setUploadErrors(prev => ({ ...prev, [type]: '' }));

        // Validate file type
        const isVideo = type === 'video';
        const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
        const validTypes = isVideo ? validVideoTypes : validImageTypes;

        if (!validTypes.includes(file.type)) {
            setUploadErrors(prev => ({
                ...prev,
                [type]: `Invalid file type. Please upload ${isVideo ? 'MP4, WebM' : 'JPG, PNG, GIF, WebP'} files.`
            }));
            return;
        }

        // Check file size (limit to 5MB for images, 50MB for videos)
        const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setUploadErrors(prev => ({
                ...prev,
                [type]: `File too large. Maximum size is ${isVideo ? '50MB' : '5MB'}.`
            }));
            return;
        }

        try {
            // Create a unique filename with timestamp
            const timestamp = Date.now();
            const sanitizedName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
            const filePath = `news/${type}s/${timestamp}_${sanitizedName}`;
            const storageRef = ref(storage, filePath);

            // Start upload with progress tracking
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on('state_changed',
                (snapshot) => {
                    // Track upload progress
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    if (type === 'image') {
                        setImgProgress(progress);
                    } else {
                        setVidProgress(progress);
                    }
                },
                (error) => {
                    // Handle upload error
                    console.error('Upload error:', error);
                    let errorMessage = 'Upload failed. Please try again.';
                    if (error.code === 'storage/unauthorized') {
                        errorMessage = 'Access Denied: Please update Firebase Storage rules.';
                    } else if (error.code === 'storage/canceled') {
                        errorMessage = 'Upload was canceled.';
                    }
                    setUploadErrors(prev => ({ ...prev, [type]: errorMessage }));
                    if (type === 'image') setImgProgress(0);
                    else setVidProgress(0);
                },
                async () => {
                    // Upload completed successfully - get download URL
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        if (type === 'image') {
                            setValue('image_url', downloadURL);
                            setTimeout(() => setImgProgress(0), 300);
                        } else {
                            setValue('video_url', downloadURL);
                            setTimeout(() => setVidProgress(0), 300);
                        }
                        console.log(`${type} uploaded successfully:`, downloadURL);
                    } catch (urlError) {
                        console.error('Error getting download URL:', urlError);
                        setUploadErrors(prev => ({ ...prev, [type]: 'Error getting file URL.' }));
                        if (type === 'image') setImgProgress(0);
                        else setVidProgress(0);
                    }
                }
            );
        } catch (error) {
            console.error('Upload setup error:', error);
            setUploadErrors(prev => ({ ...prev, [type]: 'Error starting upload. Please try again.' }));
            if (type === 'image') setImgProgress(0);
            else setVidProgress(0);
        }
    };

    const onSubmit = async (data) => {
        if (loading) return;

        try {
            setLoading(true);
            const payload = {
                ...data,
                date: new Date(data.date).toISOString(),
                image_url: data.image_url || null,
                video_url: data.video_url || null,
            };

            console.log('Submitting article:', payload);

            const response = isEditMode
                ? await api.news.update(slug, payload)
                : await api.news.create(payload);

            console.log('API Response:', response);

            if (response.ok) {
                const result = await response.json();
                console.log('Success:', result);

                // Show success toast
                toast.success(isEditMode ? 'Article updated successfully!' : '🎉 Article published! It will appear on the News page and homepage.');

                navigate('/admin/news');
            } else {
                const err = await response.json();
                console.error('API Error:', err);
                if (err.detail?.toLowerCase().includes('permission')) {
                    setLoading('perm_error');
                } else {
                    toast.error(`Save failed: ${err.detail || 'Unknown error'}`);
                }
            }
        } catch (error) {
            console.error("Submit Error:", error);
            toast.error(`Submit Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

    return (
        <AdminLayout title={isEditMode ? 'Edit Article' : 'Create New Article'}>
            <Helmet>
                <title>{isEditMode ? 'Edit Article' : 'New Article'} - Admin</title>
            </Helmet>

            <div className="max-w-5xl mx-auto pb-10 px-4 md:px-0">
                <button onClick={() => navigate('/admin/news')} className="text-gray-500 hover:text-gray-700 flex items-center gap-2 mb-6 group">
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="font-medium text-sm">Back to News List</span>
                </button>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 space-y-8">
                        {/* Header Section */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-50 pb-6">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Article Details</h2>
                                <p className="text-xs text-gray-500 mt-1">Fields marked with * are required</p>
                            </div>
                            <div className="flex items-center gap-4 bg-gray-50 px-4 py-2 rounded-xl">
                                <span className="text-sm font-semibold text-gray-700">Visibility Status:</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" {...register('is_published')} className="sr-only peer" />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2A9D8F]"></div>
                                    <span className="ml-3 text-sm font-bold text-[#2A9D8F] uppercase peer-checked:block hidden transition-all">Published</span>
                                    <span className="ml-3 text-sm font-bold text-gray-400 uppercase peer-checked:hidden transition-all">Draft</span>
                                </label>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="col-span-2">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Main Title *</label>
                                <input
                                    {...register('title', { required: 'Title is required' })}
                                    className="w-full px-5 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-[#2A9D8F]/10 focus:border-[#2A9D8F] outline-none transition-all text-lg"
                                    placeholder="Enter a catchy headline"
                                />
                                {errors.title && <p className="text-red-500 text-xs mt-1 font-bold">{errors.title.message}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Friendly URL (Slug) *</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-3 text-gray-400 text-sm">/news/</span>
                                    <input {...register('slug', { required: 'Slug is required' })} className="w-full pl-16 pr-5 py-3 border border-gray-200 rounded-xl bg-gray-50 text-sm font-medium" readOnly />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Category Selection</label>
                                <select {...register('category')} className="w-full px-5 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-[#2A9D8F]/10 focus:border-[#2A9D8F] outline-none transition-all text-sm font-medium appearance-none bg-no-repeat bg-[right_1rem_center]">
                                    <option value="News">Finance News</option>
                                    <option value="Event">Corporate Event</option>
                                    <option value="Press Release">Press Release</option>
                                    <option value="CSR">CSR Initiative</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Publish Date</label>
                                <input type="date" {...register('date')} className="w-full px-5 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-[#2A9D8F]/10 focus:border-[#2A9D8F] outline-none transition-all text-sm font-medium" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Author Name</label>
                                <input {...register('author')} className="w-full px-5 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-[#2A9D8F]/10 focus:border-[#2A9D8F] outline-none transition-all text-sm font-medium" placeholder="Ex: Admin or Content Team" />
                            </div>
                        </div>

                        {/* Media Section */}
                        <div className="bg-gray-50/50 rounded-2xl p-6 md:p-8 space-y-6">
                            <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                                <div className="bg-[#2A9D8F] p-1.5 rounded-lg">
                                    <ImageIcon className="w-5 h-5 text-white" />
                                </div>
                                <h2 className="text-lg font-bold text-gray-900">Media Content</h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <MediaUploader
                                    label="Cover Featured Image"
                                    type="image"
                                    currentUrl={imageUrl}
                                    onFileSelect={(file) => handleUpload(file, 'image')}
                                    onLinkSet={(link) => setValue('image_url', link)}
                                    onClear={() => setValue('image_url', '')}
                                    progress={imgProgress}
                                    error={uploadErrors.image}
                                />
                                <MediaUploader
                                    label="Embed Video (Optional)"
                                    type="video"
                                    currentUrl={videoUrl}
                                    onFileSelect={(file) => handleUpload(file, 'video')}
                                    onLinkSet={(link) => setValue('video_url', link)}
                                    onClear={() => setValue('video_url', '')}
                                    progress={vidProgress}
                                    error={uploadErrors.video}
                                />
                            </div>
                        </div>

                        {/* Content Section */}
                        <div className="space-y-6 pb-2">
                            <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                                <div className="bg-[#2A9D8F] p-1.5 rounded-lg">
                                    <Newspaper className="w-5 h-5 text-white" />
                                </div>
                                <h2 className="text-lg font-bold text-gray-900">Article Content</h2>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Short Teaser Summary *</label>
                                <textarea
                                    {...register('summary', { required: 'Summary is required' })}
                                    className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:ring-4 focus:ring-[#2A9D8F]/10 focus:border-[#2A9D8F] outline-none transition-all h-24 text-sm resize-none"
                                    placeholder="Brief summary that appears in the news list (max 200 chars)"
                                />
                                {errors.summary && <p className="text-red-500 text-xs mt-1 font-bold">{errors.summary.message}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Body Content (Supports simple HTML) *</label>
                                <textarea
                                    {...register('content', { required: 'Content is required' })}
                                    className="w-full px-5 py-5 border border-gray-200 rounded-xl focus:ring-4 focus:ring-[#2A9D8F]/10 focus:border-[#2A9D8F] outline-none transition-all h-80 font-mono text-sm leading-relaxed"
                                    placeholder="Write your full story here. Press Enter for new paragraphs. You can use <b>tags</b> for bold text."
                                />
                                {errors.content && <p className="text-red-500 text-xs mt-1 font-bold">{errors.content.message}</p>}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-4 pb-12">
                        <button
                            type="button"
                            onClick={() => navigate('/admin/news')}
                            className="px-8 py-3 border-2 border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-all text-sm uppercase tracking-wider"
                        >
                            Discard Changes
                        </button>
                        <button
                            type="submit"
                            disabled={loading || imgProgress > 0 || vidProgress > 0}
                            className="bg-[#2A9D8F] text-white px-10 py-3 rounded-xl font-bold hover:bg-[#238b7f] hover:shadow-lg hover:shadow-[#2A9D8F]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 text-sm uppercase tracking-wider"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin w-5 h-5 text-white" />
                                    <span>Processing...</span>
                                </>
                            ) : (
                                <>
                                    <Save className="w-5 h-5" />
                                    <span>{isEditMode ? 'Save Article' : 'Publish Article Now'}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Instruction Modal overlay shown if error occurs */}
            {Object.values(uploadErrors).some(e => e.includes('Access Denied')) && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl p-8 max-w-lg shadow-2xl border-4 border-[#2A9D8F]">
                        <div className="flex items-center gap-3 text-[#2A9D8F] mb-4">
                            <AlertCircle className="w-8 h-8" />
                            <h3 className="text-2xl font-black uppercase">Storage Permission Error</h3>
                        </div>
                        <p className="text-gray-600 mb-6 font-medium leading-relaxed">
                            Uploads are being blocked by Firebase permissions. To fix this, please copy the rules below and paste them into your **Firebase Console {'>'} Storage {'>'} Rules** tab:
                        </p>
                        <pre className="bg-gray-900 text-blue-400 p-4 rounded-xl text-xs overflow-auto mb-6 border border-white/10 shadow-inner">
                            {`rules_version = '2';
service firebase.storage {
  match /b/{bucket}/o {
    match /{allPaths=**} {
      allow read;
      allow write: if true; 
    }
  }
}`}
                        </pre>
                        <button
                            onClick={() => {
                                setUploadErrors({ image: '', video: '' });
                                setLoading(false);
                            }}
                            className="w-full bg-[#2A9D8F] text-white py-4 rounded-xl font-black uppercase tracking-widest hover:bg-[#238b7f] transition-all"
                        >
                            I Have Updated The Rules
                        </button>
                    </div>
                </div>
            )}

            {/* Firestore Permission Guide */}
            {loading === 'perm_error' && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl p-8 max-w-lg shadow-2xl border-4 border-orange-500">
                        <div className="flex items-center gap-3 text-orange-500 mb-4">
                            <AlertCircle className="w-8 h-8" />
                            <h3 className="text-2xl font-black uppercase">Database Locked</h3>
                        </div>
                        <p className="text-gray-600 mb-6 font-medium leading-relaxed">
                            Your **Firestore Database** is refusing to save the article. Please paste these rules into your **Firebase Console {'>'} Firestore Database {'>'} Rules** tab:
                        </p>
                        <pre className="bg-gray-900 text-orange-400 p-4 rounded-xl text-xs overflow-auto mb-6 border border-white/10 shadow-inner">
                            {`rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /{document=**} {
      allow read, write: if true; 
    }
  }
}`}
                        </pre>
                        <button
                            onClick={() => setLoading(false)}
                            className="w-full bg-orange-500 text-white py-4 rounded-xl font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
                        >
                            Got it, I'll update them
                        </button>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default NewsEditorPage;
